<template>
  <div class="stock-threshold-panel">
    <div class="stock-tp-header">
      <div class="stock-tp-header-icon">
        <i class="setting icon" />
      </div>
      <div>
        <div class="stock-tp-title">Stock Alert Thresholds</div>
        <div class="stock-tp-subtitle">
          Configure per-ASIN stock thresholds across your full product portfolio. These drive Low Stock &amp; Out of Stock alerts on the Alerts page.
        </div>
      </div>
      <button type="button" class="stock-tp-close" aria-label="Close" @click="requestClose">
        <i class="close icon" />
      </button>
    </div>

    <div class="stock-tp-tabs">
      <button
        type="button"
        :class="['stock-tp-tab', { active: activeTab === 'fba' }]"
        @click="activeTab = 'fba'"
      >
        <i class="cube icon" />
        FBA Stock
        <span class="stock-tp-tab-count">{{ fbaRows.length }} products</span>
      </button>
      <button
        type="button"
        :class="['stock-tp-tab', { active: activeTab === 'fbm' }]"
        @click="activeTab = 'fbm'"
      >
        <i class="home icon" />
        FBM Stock
        <span class="stock-tp-tab-count">{{ fbmRows.length }} products</span>
      </button>
    </div>

    <!-- FBA -->
    <div :class="['stock-tp-pane', { active: activeTab === 'fba' }]">
      <p v-if="fbaTotalCount > fbaRows.length" class="stock-tp-cap-note">
        Showing <strong>{{ fbaRows.length }}</strong> of <strong>{{ fbaTotalCount }}</strong> listings on this server page.
        Use <strong>Download CSV</strong> for the full catalog or adjust API pagination.
      </p>
      <div class="stock-tp-table-wrap">
        <table v-if="fbaRows.length > 0" class="stock-tp-table">
          <thead>
            <tr>
              <th rowspan="2" style="min-width: 220px">Product</th>
              <th colspan="2" class="stock-tp-th-center stock-tp-th-units-group">Alert by Units</th>
              <th colspan="2" class="stock-tp-th-center stock-tp-th-days-group">Alert by Days of Stock</th>
              <th rowspan="2" class="stock-tp-th-center stock-tp-th-interval">Avg Calc Interval</th>
            </tr>
            <tr>
              <th class="stock-tp-th-center stock-tp-th-sub-units">Warning threshold</th>
              <th class="stock-tp-th-center stock-tp-th-sub-units">Critical alert</th>
              <th class="stock-tp-th-center stock-tp-th-sub-days">Warning threshold</th>
              <th class="stock-tp-th-center stock-tp-th-sub-days">Critical alert</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="p in fbaRows"
              :key="'fba-' + listingId(p) + '-' + thresholdsDataRevision"
              :class="{ 'stock-tp-row-dirty': rowDirtyFba[listingId(p)] }"
            >
              <td>
                <span class="stock-tp-product-name" :title="p.productTitle">{{ p.productTitle }}</span>
                <div class="stock-tp-product-meta">
                  <span class="stock-tp-asin">{{ asinFor(p) }}</span>
                  <span class="stock-tp-mkt">
                    <i :class="flagClass(p.marketplace) + ' flag'" />
                    {{ marketplaceLabel(p.marketplace) }}
                  </span>
                </div>
              </td>
              <td class="stock-tp-td-center">
                <div class="stock-tp-input-cell">
                  <input
                    v-model.number="fbaForms[listingId(p)].amberThreshold"
                    type="number"
                    min="-1"
                    class="stock-tp-input"
                    :class="inputClassesFba(listingId(p), 'amberThreshold')"
                    placeholder="—"
                    @input="markFbaDirty(listingId(p))"
                    @change="markFbaDirty(listingId(p))"
                  >
                  <span class="stock-tp-input-unit">units</span>
                </div>
              </td>
              <td class="stock-tp-td-center">
                <div class="stock-tp-input-cell">
                  <input
                    v-model.number="fbaForms[listingId(p)].redThreshold"
                    type="number"
                    min="-1"
                    class="stock-tp-input"
                    :class="inputClassesFba(listingId(p), 'redThreshold')"
                    placeholder="—"
                    @input="markFbaDirty(listingId(p))"
                    @change="markFbaDirty(listingId(p))"
                  >
                  <span class="stock-tp-input-unit">units</span>
                </div>
              </td>
              <td class="stock-tp-td-center">
                <div class="stock-tp-input-cell">
                  <input
                    v-model.number="fbaForms[listingId(p)].amberDays"
                    type="number"
                    min="-1"
                    class="stock-tp-input"
                    :class="inputClassesFba(listingId(p), 'amberDays')"
                    placeholder="—"
                    @input="markFbaDirty(listingId(p))"
                    @change="markFbaDirty(listingId(p))"
                  >
                  <span class="stock-tp-input-unit">days</span>
                </div>
              </td>
              <td class="stock-tp-td-center">
                <div class="stock-tp-input-cell">
                  <input
                    v-model.number="fbaForms[listingId(p)].redDays"
                    type="number"
                    min="-1"
                    class="stock-tp-input"
                    :class="inputClassesFba(listingId(p), 'redDays')"
                    placeholder="—"
                    @input="markFbaDirty(listingId(p))"
                    @change="markFbaDirty(listingId(p))"
                  >
                  <span class="stock-tp-input-unit">days</span>
                </div>
              </td>
              <td class="stock-tp-td-center">
                <div class="stock-tp-input-cell">
                  <input
                    v-model.number="fbaForms[listingId(p)].velocityLookbackDays"
                    type="number"
                    min="1"
                    class="stock-tp-input"
                    :class="inputClassesFba(listingId(p), 'velocityLookbackDays')"
                    placeholder="14"
                    @input="markFbaDirty(listingId(p))"
                    @change="markFbaDirty(listingId(p))"
                  >
                  <span class="stock-tp-input-unit">days</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="stock-tp-empty">No FBA listings returned for thresholds.</div>
      </div>
    </div>

    <!-- FBM -->
    <div :class="['stock-tp-pane', { active: activeTab === 'fbm' }]">
      <p v-if="fbmTotalCount > fbmRows.length" class="stock-tp-cap-note">
        Showing <strong>{{ fbmRows.length }}</strong> of <strong>{{ fbmTotalCount }}</strong> FBM listings on this server page.
        Use <strong>Download CSV</strong> for the full catalog or adjust API pagination.
      </p>
      <div class="stock-tp-table-wrap">
        <table v-if="fbmRows.length > 0" class="stock-tp-table">
          <thead>
            <tr>
              <th style="min-width: 220px">Product</th>
              <th class="stock-tp-th-center stock-tp-th-sub-units">Marketplace</th>
              <th class="stock-tp-th-center stock-tp-th-sub-units">Alert below</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="p in fbmRows"
              :key="'fbm-' + listingId(p) + '-' + thresholdsDataRevision"
              :class="{ 'stock-tp-row-dirty': rowDirtyFbm[listingId(p)] }"
            >
              <td>
                <span class="stock-tp-product-name" :title="p.productTitle">{{ p.productTitle }}</span>
                <div class="stock-tp-product-meta">
                  <span class="stock-tp-asin">{{ asinFor(p) }}</span>
                  <span class="stock-tp-mkt">FBM — own warehouse</span>
                </div>
              </td>
              <td class="stock-tp-td-center">
                <span class="stock-tp-mkt">
                  <i :class="flagClass(p.marketplace) + ' flag'" />
                  {{ marketplaceLabel(p.marketplace) }}
                </span>
              </td>
              <td class="stock-tp-td-center">
                <div class="stock-tp-input-cell">
                  <input
                    v-model.number="fbmForms[listingId(p)].threshold"
                    type="number"
                    min="0"
                    class="stock-tp-input"
                    :class="inputClassesFbmObj(listingId(p))"
                    placeholder="—"
                    @input="markFbmDirty(listingId(p))"
                    @change="markFbmDirty(listingId(p))"
                  >
                  <span class="stock-tp-input-unit">units</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="stock-tp-empty">No FBM listings returned for thresholds.</div>
      </div>
    </div>

    <div class="stock-tp-csv-strip">
      <div class="stock-tp-csv-label">
        <i class="info circle icon" />
        <span>Managing a large catalog? <strong>Download as CSV</strong> → edit thresholds in bulk → re-upload.</span>
      </div>
      <div class="stock-tp-csv-actions">
        <button
          type="button"
          class="stock-tp-btn-csv stock-tp-btn-csv-dl"
          :disabled="csvDownloading"
          @click="downloadCsv"
        >
          <i :class="csvDownloading ? 'notched circle loading icon' : 'download icon'" />
          {{ csvDownloading ? 'Downloading…' : 'Download CSV' }}
        </button>
        <span class="stock-tp-csv-divider" />
        <label class="stock-tp-btn-csv stock-tp-btn-csv-ul">
          <i class="upload icon" />
          Upload CSV
          <input
            type="file"
            accept=".csv,text/csv,application/csv"
            class="stock-tp-csv-file"
            @change="uploadCsv"
          >
        </label>
      </div>
    </div>

    <div :class="['stock-tp-dirty-banner', { visible: dirty }]">
      <i class="exclamation triangle icon" />
      You have unsaved changes.
    </div>

    <div v-if="thresholdsLoading" class="stock-tp-loading-banner">
      <i class="notched circle loading icon" />
      Loading thresholds…
    </div>
    <div v-if="thresholdsLoadError && !thresholdsLoading" class="stock-tp-panel-error stock-tp-load-warning">
      {{ thresholdsLoadError }}
    </div>

    <div class="stock-tp-footer">
      <div class="stock-tp-footer-note">
        <i class="info circle icon" />
        Leave any field blank to use account defaults (14 days for Low Stock). Thresholds apply to both the Stock page status and Alerts page triggers.
      </div>
      <div class="stock-tp-footer-actions">
        <button type="button" class="stock-tp-btn-cancel" @click="requestClose">Cancel</button>
        <button
          type="button"
          class="stock-tp-btn-save"
          :disabled="saving || !hasRowsToSaveOnActiveTab"
          @click="saveAll"
        >
          <i v-if="saving" class="notched circle loading icon" />
          Save All Thresholds
        </button>
      </div>
    </div>

    <div v-if="saveError" class="stock-tp-panel-error">{{ saveError }}</div>
    <div v-if="csvMessage" :class="csvMessageIsError ? 'stock-tp-panel-error' : 'stock-tp-csv-toast'">{{ csvMessage }}</div>
  </div>
</template>

<script>
import './styles/StockAlertThresholdsPanel.css'
import {
  listingIdFromProduct,
  extractThresholdGetItems,
  productIdFromThresholdRow,
  parseJsonIfNeeded,
  httpResponseEntity
} from '../../utils/stockAlertThresholdsApiParse'

/** Display row built only from GET /stock-alert-thresholds list items (no separate product API). */
function displayRowFromThresholdItem (row) {
  var id = productIdFromThresholdRow(row)
  if (!id) return null
  var title = (row.productTitle || row.product_title || row.productName || row.product_name || row.title || '')
  if (typeof title === 'string') title = title.trim()
  if (!title) {
    var asin = row.asin || ''
    title = asin ? ('ASIN ' + asin) : ('Listing #' + id)
  }
  return {
    productId: id,
    productTitle: title,
    asin: row.asin || '',
    marketplace: row.marketplace || ''
  }
}

function bodyObjectFromResponse (rawBody) {
  var b = parseJsonIfNeeded(rawBody)
  if (b == null) b = rawBody
  if (b != null && typeof b === 'object' && !Array.isArray(b)) return b
  return {}
}

/** Unwrap strings, BigDecimal-style objects, Mongo extended JSON, etc. */
function unwrapApiNumber (v) {
  if (v === undefined || v === null || v === '') return null
  if (typeof v === 'number' && !isNaN(v)) return v
  if (typeof v === 'string') {
    var t = v.trim()
    if (t === '') return null
    var n = parseFloat(t)
    return isNaN(n) ? null : n
  }
  if (typeof v === 'object' && v !== null) {
    if ('value' in v) return unwrapApiNumber(v.value)
    if ('$numberDecimal' in v) return unwrapApiNumber(v.$numberDecimal)
  }
  var x = Number(v)
  return isNaN(x) ? null : x
}

function numNonNeg (v) {
  var n = unwrapApiNumber(v)
  if (n === null) return null
  return n >= 0 ? n : null
}

function numPos (v) {
  var n = unwrapApiNumber(v)
  if (n === null) return null
  return n > 0 ? n : null
}

function pick (a, b) {
  return (a !== undefined && a !== null) ? a : b
}

function emptyFbaForm () {
  return {
    amberThreshold: null,
    redThreshold: null,
    amberDays: null,
    redDays: null,
    velocityLookbackDays: null
  }
}

function formFromFbaSettings (s) {
  s = s || {}
  var f = emptyFbaForm()
  f.amberThreshold = numNonNeg(pick(s.amberThreshold, s.amber_threshold))
  f.redThreshold = numNonNeg(pick(s.redThreshold, s.red_threshold))
  f.amberDays = numNonNeg(pick(s.amberDays, s.amber_days))
  f.redDays = numNonNeg(pick(s.redDays, s.red_days))
  f.velocityLookbackDays = numPos(pick(s.velocityLookbackDays, s.velocity_lookback_days))
  return f
}

function toPayloadVal (v) {
  var n = unwrapApiNumber(v)
  return n
}

function fbmThresholdFromSettings (s) {
  if (!s || typeof s !== 'object') return null
  var t = pick(s.threshold, pick(s.fbm_threshold, pick(s.redThreshold, s.red_threshold)))
  var n = unwrapApiNumber(t)
  return n !== null && n >= 0 ? n : null
}

export default {
  name: 'StockAlertThresholdsPanel',
  props: {
    /** When true, panel is visible — triggers list GET (avoids relying on parent $refs). */
    panelOpen: { type: Boolean, default: false },
    /** Base URL for stock alert thresholds API (GET list, PATCH bulk, CSV import/export). */
    thresholdsApiBase: { type: String, default: '/api/v1/stock-alert-thresholds' },
    /**
     * When exactly one seller account is selected in the Stock page navbar, pass its id so PATCH/CSV scope writes.
     * List GET intentionally omits this so it matches unscoped `?channel=fba` (seller filter often returns empty).
     */
    sellerAccountId: { type: String, default: null }
  },
  data: function () {
    return {
      activeTab: 'fba',
      fbaForms: {},
      fbmForms: {},
      /** Cached settings from GET /stock-alert-thresholds, keyed by productId string */
      fbaSettingsByProductId: {},
      fbmSettingsByProductId: {},
      rowDirtyFba: {},
      rowDirtyFbm: {},
      dirty: false,
      saving: false,
      saveError: null,
      csvMessage: '',
      csvMessageIsError: false,
      _csvToastTimer: null,
      thresholdsLoading: false,
      thresholdsLoadError: null,
      _fetchSeq: 0,
      csvDownloading: false,
      /** Bumped after each successful thresholds load so number inputs remount (avoids stuck empty v-model). */
      thresholdsDataRevision: 0,
      /** Rows from GET /stock-alert-thresholds?channel=fba (no product_ids). */
      fbaListRows: [],
      fbmListRows: [],
      /** Server `total` when present; else same as row count. */
      fbaListTotal: null,
      fbmListTotal: null
    }
  },
  computed: {
    fbaRows: function () {
      return this.fbaListRows
    },
    fbmRows: function () {
      return this.fbmListRows
    },
    fbaTotalCount: function () {
      if (this.fbaListTotal != null && !isNaN(Number(this.fbaListTotal))) return Number(this.fbaListTotal)
      return this.fbaListRows.length
    },
    fbmTotalCount: function () {
      if (this.fbmListTotal != null && !isNaN(Number(this.fbmListTotal))) return Number(this.fbmListTotal)
      return this.fbmListRows.length
    },
    hasDirtyThresholds: function () {
      if (this.activeTab === 'fba') {
        for (var id in this.rowDirtyFba) {
          if (this.rowDirtyFba[id]) return true
        }
        return false
      }
      for (var id2 in this.rowDirtyFbm) {
        if (this.rowDirtyFbm[id2]) return true
      }
      return false
    },
    /** Save includes all rows returned for the active tab from the thresholds list GET. */
    hasRowsToSaveOnActiveTab: function () {
      if (this.activeTab === 'fba') return this.fbaRows.length > 0
      return this.fbmRows.length > 0
    }
  },
  watch: {
    panelOpen: function (open) {
      if (!open) return
      this.reloadThresholdsFromApi()
    },
    sellerAccountId: function () {
      if (!this.panelOpen) return
      this.ensureFormShells()
      // Same as opening: debounced fetch races _fetchSeq and skips thresholds-load-complete — use one load path.
      this.reloadThresholdsFromApi()
    }
  },
  created: function () {
    this.ensureFormShells()
  },
  mounted: function () {
    this.ensureFormShells()
  },
  methods: {
    httpParams: function () {
      var p = {}
      if (this.sellerAccountId) p.seller_account_id = this.sellerAccountId
      return p
    },
    /** GET list only — no seller_account_id (matches working `?channel=fba` in browser). */
    listGetParams: function () {
      return {}
    },
    /**
     * Call when the panel is opened: ensure row shells exist, then GET thresholds.
     * Exposed for parent (Stock page) so settings load on demand, not only from mount/watch.
     */
    reloadThresholdsFromApi: function () {
      this.ensureFormShells()
      return this.loadThresholdsFromApi()
    },
    loadThresholdsFromApi: function () {
      var self = this
      this._fetchSeq += 1
      var seq = this._fetchSeq
      this.thresholdsLoading = true
      this.thresholdsLoadError = null
      var errFba = null
      var errFbm = null
      var finish = function () {
        if (seq !== self._fetchSeq) return
        self.thresholdsLoading = false
        if (errFba && errFbm) {
          self.thresholdsLoadError = (errFba.body && errFba.body.message) || errFba.statusText || 'Could not load alert thresholds'
        } else if (errFba || errFbm) {
          self.thresholdsLoadError = 'Some threshold data could not be loaded (e.g. API not available). Edits may save once the server exposes this endpoint.'
        } else {
          self.thresholdsLoadError = null
        }
        self.syncAllForms(!!self.hasDirtyThresholds)
        if (self.activeTab === 'fbm' && self.fbmRows.length === 0 && self.fbaRows.length > 0) {
          self.activeTab = 'fba'
        }
      }
      // FBA then FBM: parallel + slow/hanging FBM blocked the Stock page “Loading…” bar; top-level JSON array is fine for extractThresholdGetItems.
      return this.fetchThresholdsChannel('fba')
        .catch(function (e) {
          errFba = e
        })
        .then(function () {
          return self.fetchThresholdsChannel('fbm').catch(function (e) {
            errFbm = e
          })
        })
        .then(function () {
          finish()
        })
        .catch(function () {
          if (seq !== self._fetchSeq) return
          self.thresholdsLoading = false
          self.thresholdsLoadError = 'Could not load alert thresholds'
        })
        .then(function () {
          // Unblock parent prefetch UI even if this round was superseded (avoids infinite “Loading…” on _fetchSeq races).
          self.$emit('thresholds-load-complete')
        })
    },
    /**
     * Apply parsed GET list body to FBA/FBM state (shared by primary GET and 304 retry).
     */
    applyThresholdsListPayload: function (channel, res) {
      var entity = httpResponseEntity(res)
      var bodyObj = bodyObjectFromResponse(entity)
      var items = extractThresholdGetItems(entity)
      var merge = {}
      var listRows = []
      var i
      for (i = 0; i < items.length; i++) {
        var row = items[i]
        var pid = productIdFromThresholdRow(row)
        if (!pid) continue
        var dr = displayRowFromThresholdItem(row)
        if (dr) listRows.push(dr)
        var raw = row.settings
        if (raw == null) raw = {}
        else if (typeof raw === 'string') {
          try {
            raw = JSON.parse(raw)
          } catch (e) {
            raw = {}
          }
        }
        merge[pid] = typeof raw === 'object' && raw !== null ? raw : {}
      }
      var total = bodyObj.total != null ? Number(bodyObj.total) : null
      if (channel === 'fba') {
        this.fbaListRows = listRows
        this.fbaListTotal = total
        this.fbaSettingsByProductId = merge
      } else {
        this.fbmListRows = listRows
        this.fbmListTotal = total
        this.fbmSettingsByProductId = merge
      }
      // Keep forms in sync with rows as soon as each channel returns (finish() can be skipped on _fetchSeq races).
      this.syncAllForms(!!this.hasDirtyThresholds)
    },
    listGetRequestOptions: function (channel, extraParams) {
      return {
        params: Object.assign({}, this.listGetParams(), { channel: channel, _skcb: Date.now() }, extraParams || {}),
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        },
        timeout: 45000
      }
    },
    /**
     * GET {thresholdsApiBase}?channel=… only (no product_ids). Rows and settings come from response `items`.
     * Busts cache so we avoid 304 (vue-resource treats 304 as !ok and rejects; body is often empty).
     */
    fetchThresholdsChannel: function (channel) {
      var self = this
      var base = this.thresholdsApiBase
      var run = function (extra) {
        return self.$http.get(base, self.listGetRequestOptions(channel, extra)).then(function (res) {
          self.applyThresholdsListPayload(channel, res)
        })
      }
      return run(null).catch(function (res) {
        if (res && res.status === 304) {
          return run({ _skretry: Date.now() })
        }
        return Promise.reject(res)
      })
    },
    mergeSettingsIntoCache: function (channel, updates) {
      var map = channel === 'fba' ? Object.assign({}, this.fbaSettingsByProductId) : Object.assign({}, this.fbmSettingsByProductId)
      for (var u = 0; u < updates.length; u++) {
        var up = updates[u]
        var pid = String(up.product_id)
        var prev = map[pid] || {}
        var next = Object.assign({}, prev)
        var keys = Object.keys(up.settings || {})
        for (var k = 0; k < keys.length; k++) {
          var key = keys[k]
          next[key] = up.settings[key]
        }
        map[pid] = next
      }
      if (channel === 'fba') this.fbaSettingsByProductId = map
      else this.fbmSettingsByProductId = map
    },
    asinFor: function (product) {
      if (product.asin) return product.asin
      if (product.productUrl) {
        var m = product.productUrl.match(/\/dp\/([A-Z0-9]+)/i)
        if (m) return m[1]
      }
      return listingIdFromProduct(product) || ''
    },
    listingId: function (product) {
      return listingIdFromProduct(product)
    },
    marketplaceLabel: function (mp) {
      if (!mp) return ''
      var m = String(mp).toUpperCase()
      var map = { US: 'US', UK: 'UK', CA: 'CA', DE: 'DE', FR: 'FR', ES: 'ES', IT: 'IT', MX: 'MX', BR: 'BR', IN: 'IN', NL: 'NL', GB: 'UK' }
      return map[m] || m
    },
    flagClass: function (marketplace) {
      if (!marketplace) return 'us'
      var m = String(marketplace).toLowerCase()
      return (m === 'uk' ? 'gb' : m).replace('uk', 'gb')
    },
    /** Add missing form keys for each row from the thresholds list GET. */
    ensureFormShells: function () {
      var i
      for (i = 0; i < this.fbaRows.length; i++) {
        var p = this.fbaRows[i]
        var id = listingIdFromProduct(p)
        if (!id || this.fbaForms[id]) continue
        this.$set(this.fbaForms, id, formFromFbaSettings(this.fbaSettingsByProductId[id]))
      }
      for (i = 0; i < this.fbmRows.length; i++) {
        var q = this.fbmRows[i]
        var id2 = listingIdFromProduct(q)
        if (!id2 || this.fbmForms[id2]) continue
        this.$set(this.fbmForms, id2, { threshold: fbmThresholdFromSettings(this.fbmSettingsByProductId[id2]) })
      }
    },
    /**
     * Rebuild forms from cached API settings.
     * @param {boolean} preserveDirty — if true, keep user-edited rows and dirty flags (use after GET completes while user may be typing).
     * @param {string|null} dirtyClearScope — when preserveDirty is false: if `'fba'` or `'fbm'`, clear dirty only for that channel (after partial save); else clear both.
     */
    syncAllForms: function (preserveDirty, dirtyClearScope) {
      preserveDirty = !!preserveDirty
      dirtyClearScope = dirtyClearScope || null
      var fba = {}
      var fbm = {}
      var i
      var self = this
      function keepFbaUserForm (id) {
        if (preserveDirty && self.rowDirtyFba[id] && self.fbaForms[id]) return true
        // After FBM-only save, do not overwrite in-progress FBA rows.
        if (!preserveDirty && dirtyClearScope === 'fbm' && self.rowDirtyFba[id] && self.fbaForms[id]) return true
        return false
      }
      function keepFbmUserForm (id) {
        if (preserveDirty && self.rowDirtyFbm[id] && self.fbmForms[id]) return true
        // After FBA-only save, do not overwrite in-progress FBM rows.
        if (!preserveDirty && dirtyClearScope === 'fba' && self.rowDirtyFbm[id] && self.fbmForms[id]) return true
        return false
      }
      for (i = 0; i < this.fbaRows.length; i++) {
        var p = this.fbaRows[i]
        var id = listingIdFromProduct(p)
        if (!id) continue
        if (keepFbaUserForm(id)) {
          fba[id] = Object.assign({}, this.fbaForms[id])
        } else {
          fba[id] = formFromFbaSettings(this.fbaSettingsByProductId[id])
        }
      }
      for (i = 0; i < this.fbmRows.length; i++) {
        var q = this.fbmRows[i]
        var id2 = listingIdFromProduct(q)
        if (!id2) continue
        if (keepFbmUserForm(id2)) {
          fbm[id2] = Object.assign({}, this.fbmForms[id2])
        } else {
          fbm[id2] = { threshold: fbmThresholdFromSettings(this.fbmSettingsByProductId[id2]) }
        }
      }
      this.fbaForms = fba
      this.fbmForms = fbm
      if (!preserveDirty) {
        if (dirtyClearScope === 'fba') {
          this.rowDirtyFba = {}
        } else if (dirtyClearScope === 'fbm') {
          this.rowDirtyFbm = {}
        } else {
          this.rowDirtyFba = {}
          this.rowDirtyFbm = {}
        }
        this.saveError = null
        var anyDirtyAfterClear = false
        for (var k in this.rowDirtyFba) {
          if (this.rowDirtyFba[k]) { anyDirtyAfterClear = true; break }
        }
        if (!anyDirtyAfterClear) {
          for (var k2 in this.rowDirtyFbm) {
            if (this.rowDirtyFbm[k2]) { anyDirtyAfterClear = true; break }
          }
        }
        this.dirty = anyDirtyAfterClear
      } else {
        var anyDirty = false
        for (var k3 in this.rowDirtyFba) {
          if (this.rowDirtyFba[k3]) { anyDirty = true; break }
        }
        if (!anyDirty) {
          for (var k4 in this.rowDirtyFbm) {
            if (this.rowDirtyFbm[k4]) { anyDirty = true; break }
          }
        }
        this.dirty = anyDirty
      }
    },
    markFbaDirty: function (productId) {
      this.$set(this.rowDirtyFba, String(productId), true)
      this.dirty = true
    },
    markFbmDirty: function (productId) {
      this.$set(this.rowDirtyFbm, String(productId), true)
      this.dirty = true
    },
    inputClassesFba: function (productId, field) {
      var id = String(productId)
      var form = this.fbaForms[id]
      if (!form) return ''
      var v = form[field]
      var filled = (v !== null && v !== undefined && v !== '' && !isNaN(Number(v)))
      var dirty = this.rowDirtyFba[id]
      return {
        'stock-tp-input-filled': filled,
        'stock-tp-input-dirty': dirty
      }
    },
    inputClassesFbmObj: function (productId) {
      var id = String(productId)
      var form = this.fbmForms[id]
      if (!form) return {}
      var v = form.threshold
      var filled = (v !== null && v !== undefined && v !== '' && !isNaN(Number(v)))
      var dirty = this.rowDirtyFbm[id]
      return {
        'stock-tp-input-filled': filled,
        'stock-tp-input-dirty': dirty
      }
    },
    buildFbaPayload: function (form) {
      var vel = form.velocityLookbackDays
      var velocityLookbackDays = (vel !== null && vel !== '' && !isNaN(Number(vel))) ? Number(vel) : null
      return {
        settings: {
          amber_threshold: toPayloadVal(form.amberThreshold),
          red_threshold: toPayloadVal(form.redThreshold),
          amber_days: toPayloadVal(form.amberDays),
          red_days: toPayloadVal(form.redDays),
          velocity_lookback_days: velocityLookbackDays
        }
      }
    },
    /**
     * PATCH {thresholdsApiBase} — one request per Save click, active tab only.
     * Updates[] = all rows in the active tab (from the list GET).
     */
    saveAll: function () {
      var self = this
      this.saving = true
      this.saveError = null
      var channel = this.activeTab === 'fba' ? 'fba' : 'fbm'
      var updates = []
      if (this.activeTab === 'fba') {
        for (var i = 0; i < this.fbaRows.length; i++) {
          var p = this.fbaRows[i]
          var id = listingIdFromProduct(p)
          if (!id) continue
          var form = this.fbaForms[id] || formFromFbaSettings(this.fbaSettingsByProductId[id])
          updates.push({
            product_id: id,
            settings: this.buildFbaPayload(form).settings
          })
        }
      } else {
        for (var j = 0; j < this.fbmRows.length; j++) {
          var q = this.fbmRows[j]
          var id2 = listingIdFromProduct(q)
          if (!id2) continue
          var fform = this.fbmForms[id2] || { threshold: fbmThresholdFromSettings(this.fbmSettingsByProductId[id2]) }
          var v = fform.threshold
          var threshold = (v !== null && v !== '' && !isNaN(Number(v))) ? Number(v) : null
          updates.push({
            product_id: id2,
            settings: { threshold: threshold }
          })
        }
      }
      if (updates.length === 0) {
        this.saving = false
        return
      }
      this.$http.patch(this.thresholdsApiBase, { channel: channel, updates: updates }, { params: this.httpParams(), timeout: 60000 }).then(function (res) {
        var body = res.body || {}
        var results = body.results || []
        var failed = body.failed != null ? Number(body.failed) : results.filter(function (x) { return x && x.ok === false }).length
        if (failed > 0) {
          var firstErr = results.filter(function (x) { return x && x.ok === false })[0]
          self.saveError = (firstErr && firstErr.error) ? String(firstErr.error) : ('Some rows failed to save (' + failed + ')')
          self.saving = false
          return
        }
        self.mergeSettingsIntoCache(channel, updates)
        self.saving = false
        self.syncAllForms(false, channel)
        self.$emit('saved')
      }, function (err) {
        self.saving = false
        self.saveError = (err.body && err.body.message) || 'Failed to save thresholds'
      })
    },
    downloadCsv: function () {
      var self = this
      var ch = this.activeTab === 'fba' ? 'fba' : 'fbm'
      var qs = []
      qs.push('channel=' + encodeURIComponent(ch))
      if (this.sellerAccountId) qs.push('seller_account_id=' + encodeURIComponent(this.sellerAccountId))
      var exportUrl = this.thresholdsApiBase + '/export.csv?' + qs.join('&')
      var defaultName = 'stock-thresholds-' + ch + '-' + new Date().toISOString().slice(0, 10) + '.csv'

      this.csvDownloading = true
      var xhr = new XMLHttpRequest()
      xhr.open('GET', exportUrl, true)
      xhr.responseType = 'blob'
      xhr.onload = function () {
        self.csvDownloading = false
        if (xhr.status === 200) {
          var blob = xhr.response
          var url = window.URL.createObjectURL(blob)
          var link = document.createElement('a')
          link.href = url
          var cd = xhr.getResponseHeader('Content-Disposition')
          var filename = defaultName
          if (cd) {
            var m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (m && m[1]) filename = m[1].replace(/['"]/g, '').trim() || defaultName
          }
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } else {
          var reader = new FileReader()
          reader.onload = function () {
            var msg = 'Could not download CSV.'
            try {
              var j = JSON.parse(reader.result)
              if (j.message) msg = j.message
            } catch (e) { /* ignore */ }
            self.showCsvToast(msg, true)
          }
          reader.readAsText(xhr.response)
        }
      }
      xhr.onerror = function () {
        self.csvDownloading = false
        self.showCsvToast('Download failed. Check your connection and try again.', true)
      }
      xhr.send()
    },
    isCsvFile: function (file) {
      if (!file || !file.name) return false
      if (!/\.csv$/i.test(file.name)) return false
      if (!file.type) return true
      return file.type === 'text/csv' ||
        file.type === 'application/csv' ||
        file.type === 'text/plain' ||
        file.type === 'application/octet-stream'
    },
    uploadCsv: function (e) {
      var input = e.target
      if (!input.files || !input.files[0]) return
      var file = input.files[0]
      if (!this.isCsvFile(file)) {
        this.showCsvToast('Please choose a CSV file (.csv).', true)
        input.value = ''
        return
      }
      var ch = this.activeTab === 'fba' ? 'fba' : 'fbm'
      var formData = new window.FormData()
      formData.append('file', file, file.name || 'thresholds-import.csv')
      var self = this
      var q = ['channel=' + encodeURIComponent(ch)]
      if (this.sellerAccountId) q.push('seller_account_id=' + encodeURIComponent(this.sellerAccountId))
      this.$http.post(this.thresholdsApiBase + '/import.csv?' + q.join('&'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(function (res) {
        var b = res.body || {}
        var errList = b.errors || []
        var failed = b.failed != null ? Number(b.failed) : errList.length
        if (failed > 0) {
          self.showCsvToast('Import finished with ' + failed + ' error(s). Check your CSV and try again.', true)
        } else {
          self.showCsvToast('CSV imported. Thresholds will refresh below.', false)
        }
        self.loadThresholdsFromApi()
        self.$emit('spreadsheet-imported')
        input.value = ''
      }, function (err) {
        self.showCsvToast((err.body && err.body.message) || 'Import failed. Check the file format and try again.', true)
        input.value = ''
      })
    },
    showCsvToast: function (msg, isError) {
      var self = this
      if (this._csvToastTimer) clearTimeout(this._csvToastTimer)
      this.csvMessage = msg
      this.csvMessageIsError = isError
      this._csvToastTimer = setTimeout(function () {
        self.csvMessage = ''
      }, 5000)
    },
    requestClose: function () {
      if (this.dirty) {
        if (!window.confirm('Discard unsaved changes?')) return
      }
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.stock-tp-loading-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 13px;
  color: #334155;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.stock-tp-load-warning {
  margin: 0 20px 8px;
}
.stock-tp-csv-toast {
  padding: 10px 20px;
  font-size: 13px;
  color: #15803d;
  background: #ecfdf5;
  border-top: 1px solid #bbf7d0;
}
</style>



// WEBPACK FOOTER //
// StockAlertThresholdsPanel.vue?4f191716